import {
  ClaimOwnerConfig,
  getClaimOwnerConfig,
  putClaimOwnerConfig,
} from '@dispatch/shared/services';
import {
  useMutation,
  useQuery as useReactQuery,
  useQueryClient,
} from 'react-query';

const FIVE_MINUTES = 5 * 60 * 1000;

const claimOwnerConfigKey = (tenantId?: string) => [
  'claimowner-config',
  tenantId,
];

/**
 * useClaimOwnerConfig - React Query wrapper for the tenant claimowner
 * configuration document.
 */
export const useClaimOwnerConfig = (tenantId?: string) => {
  return useReactQuery<ClaimOwnerConfig>(
    claimOwnerConfigKey(tenantId),
    () => getClaimOwnerConfig(tenantId as string),
    {
      enabled: !!tenantId,
      staleTime: FIVE_MINUTES,
    }
  );
};

/**
 * useUpsertClaimOwnerConfig - mutation for the tenant claimowner
 * configuration. The Configuration PUT is a full replacement, so callers
 * must pass the complete value object.
 */
export const useUpsertClaimOwnerConfig = (
  tenantId?: string,
  onSuccess?: (value: ClaimOwnerConfig) => void
) => {
  const client = useQueryClient();
  const { mutate, status } = useMutation(
    ['upsert-claimowner-config', tenantId],
    async (value: ClaimOwnerConfig) => {
      if (!tenantId) {
        throw new Error('tenantId is required to update claimowner config');
      }
      await putClaimOwnerConfig(tenantId, value);
      return value;
    },
    {
      onSuccess,
      onSettled: () => {
        client.invalidateQueries(claimOwnerConfigKey(tenantId));
      },
    }
  );

  return {
    upsertClaimOwnerConfig: mutate,
    status,
  };
};
